import Component from '@glimmer/component'
import didInsert from '@ember/render-modifiers/modifiers/did-insert'
import { action } from '@ember/object'
import { tracked } from '@glimmer/tracking'
import { next } from '@ember/runloop'
import { service } from '@ember/service'
import { registerDestructor } from '@ember/destroyable'

interface LayoutTwoColumnSignature {
  Args: {
    stickyColumn?: 'left' | 'right'
    constrainHeight?: 'left' | 'right' | 'both'
    fillHeight?: boolean
  }
  Blocks: {
    leftColumn: []
    rightColumn: []
    staticFooter: []
    stickyFooter: []
  }
  Element: HTMLDivElement
}

const DEFAULT_STICKY_VIEWPORT_OFFSET = 198

/**
 * A responsive two-column layout component with optional footer sections.
 *
 * This component provides a grid-based layout that adapts from single column on mobile
 * to a two-column layout on medium screens and larger. It supports optional static and
 * sticky footer blocks.
 * Columns can be made sticky on large screens if the content does not overflow the viewport.
 */
export default class LayoutTwoColumnComponent extends Component<LayoutTwoColumnSignature> {
  @service frameManager: FrameManager

  @tracked containerRef: HTMLElement | null = null
  @tracked leftColumnRef: HTMLElement | null = null
  @tracked rightColumnRef: HTMLElement | null = null
  @tracked stickyFooterRef: HTMLElement | null = null
  @tracked layoutViewportOffset = DEFAULT_STICKY_VIEWPORT_OFFSET

  // Columns grow after first render — a cart summary appears, a step expands — so the
  // sticky decision has to be remeasured rather than settled once on insert.
  @tracked leftColumnHeight = 0
  @tracked rightColumnHeight = 0
  @tracked viewportHeight = 0

  private resizeObserver?: ResizeObserver

  /**
   * Height a pinned column can occupy without disappearing behind the sticky
   * footer or the grid's own top padding.
   */
  get availableColumnHeight() {
    return this.viewportHeight - this.layoutViewportOffset
  }

  get stickyLeftColumn() {
    if (this.constrainLeftColumnHeight) {
      return this.args.stickyColumn === 'left'
    }

    const overflow = this.leftColumnHeight - this.availableColumnHeight
    return this.args.stickyColumn === 'left' && overflow <= 0
  }

  get stickyRightColumn() {
    if (this.constrainRightColumnHeight) {
      return this.args.stickyColumn === 'right'
    }

    const overflow = this.rightColumnHeight - this.availableColumnHeight
    return this.args.stickyColumn === 'right' && overflow <= 0
  }

  /**
   * Row template for the grid. `fillHeight` gives the column row the free space
   * so the footer sits at the bottom of a short page instead of following the
   * content up.
   */
  get gridRows() {
    if (this.args.fillHeight) {
      return 'grid-rows-[auto_1fr_auto] @md/flow:grid-rows-[1fr_auto]'
    }

    return 'grid-rows-[auto_auto_auto] @md/flow:grid-rows-[auto_auto]'
  }

  get constrainLeftColumnHeight() {
    return this.args.constrainHeight === 'left' || this.args.constrainHeight === 'both'
  }

  get constrainRightColumnHeight() {
    return this.args.constrainHeight === 'right' || this.args.constrainHeight === 'both'
  }

  /**
   * Calculates the available height for the columns based on the header, footer and padding.
   */
  calculateLayoutViewportOffset() {
    const header = document.querySelector('[data-testid="booking-flow-container__header"]')
    let offset = DEFAULT_STICKY_VIEWPORT_OFFSET

    if (this.containerRef && header) {
      const styles = getComputedStyle(this.containerRef)
      const paddingTop = parseFloat(styles.paddingTop)
      const gridGap = parseFloat(styles.gap)
      const stickyFooterHeight = this.stickyFooterRef?.clientHeight ?? 0

      offset = paddingTop + gridGap + stickyFooterHeight

      // if we're inside an iframe, we need to account for the inner container for the widget, inside that iframe
      if (this.frameManager.isInFrame) {
        const iframeContainer = document.querySelector('[data-testid="flow-container"] [data-testid="flow"]')
        const iframeOffset = iframeContainer
          ? parseFloat((getComputedStyle(iframeContainer).marginTop) ?? '0')
          : 0

        offset += iframeOffset
      }
    }

    this.layoutViewportOffset = offset

    // set css properties
    this.containerRef?.style.setProperty('--layout-viewport-offset', `${offset}px`)
    this.containerRef?.style.setProperty('--sticky-footer-height', `${this.stickyFooterRef?.clientHeight ?? 0}px`)

    const aboveGridOffset = this.containerRef
      ? this.containerRef.getBoundingClientRect().top + window.scrollY
      : 0
    this.containerRef?.style.setProperty('--above-grid-offset', `${aboveGridOffset}px`)
  }

  /** Remeasures the columns and the space they have to fill. */
  @action
  measure() {
    this.viewportHeight = window.innerHeight
    this.leftColumnHeight = this.leftColumnRef?.scrollHeight ?? 0
    this.rightColumnHeight = this.rightColumnRef?.scrollHeight ?? 0
    this.calculateLayoutViewportOffset()
  }

  @action
  setup(element: HTMLElement) {
    this.containerRef = element
    this.leftColumnRef = element.querySelector('[data-testid="layout-kit__two-column__left-column"]')
    this.rightColumnRef = element.querySelector('[data-testid="layout-kit__two-column__right-column"]')
    this.stickyFooterRef = element.querySelector('[data-testid="layout-kit__two-column__sticky-footer"]')

    this.resizeObserver = new ResizeObserver(() => {
      next(() => this.measure())
    })

    for (const observed of [this.leftColumnRef, this.rightColumnRef, this.stickyFooterRef]) {
      if (observed) this.resizeObserver.observe(observed)
    }

    window.addEventListener('resize', this.measure)

    registerDestructor(this, () => {
      this.resizeObserver?.disconnect()
      window.removeEventListener('resize', this.measure)
    })

    next(() => {
      this.measure()
    })
  }

  <template>
    <div
      id='layout-kit__two-column'
      data-testid='layout-kit__two-column'
      class='grid h-[calc(100dvh-var(--above-grid-offset,0px))] embedded:h-auto in-frame:h-auto w-full z-10 grid-cols-1 items-start justify-items-center gap-4 @sm/flow:pt-4 @md/flow:grid-cols-[1fr_minmax(402px,_637px)_402px_1fr]
        {{this.gridRows}}'
      {{didInsert this.setup}}
      ...attributes
    >
      <div
        data-testid='layout-kit__two-column__left-column'
        class='w-full max-w-132 @md/flow:col-start-2 @md/flow:max-w-none
          {{if this.stickyLeftColumn "@md/flow:sticky @md/flow:top-4"}}
          {{if this.constrainLeftColumnHeight "@sm/flow:h-[calc(100dvh-var(--layout-viewport-offset))] embedded:h-auto"}}
          '
      >
        {{yield to='leftColumn'}}
      </div>

      <div
        data-testid='layout-kit__two-column__right-column'
        class='w-full max-w-132 @md/flow:max-w-none
          {{if this.stickyRightColumn "@md/flow:sticky @md/flow:top-4"}}
          {{if this.constrainRightColumnHeight "@sm/flow:h-[calc(100dvh-var(--layout-viewport-offset))]"}}
          '
      >
        {{yield to='rightColumn'}}
      </div>

      {{#if (has-block 'staticFooter')}}
        <div
          data-testid='layout-kit__two-column__static-footer'
          class='w-full self-end [grid-area:3_/_1_/_3_/_1] @md/flow:[grid-area:2_/_1_/_2_/_5]'
        >
          {{yield to='staticFooter'}}
        </div>
      {{/if}}

      {{#if (has-block 'stickyFooter')}}
        <div
          data-testid='layout-kit__two-column__sticky-footer'
          class='pointer-events-none sticky bottom-0 w-full self-end [grid-area:3_/_1_/_3_/_1] @md/flow:[grid-area:2_/_1_/_2_/_5]'
        >
          {{yield to='stickyFooter'}}
        </div>
      {{/if}}

    </div>
    {{yield to='raw'}}
  </template>
}
